import React from 'react';
import { Table, Input, Tooltip, Button, Space } from 'antd';
import { Store, Search, Check, Ban, Undo2, Package, MousePointerClick, TriangleAlert } from 'lucide-react';
import dayjs from 'dayjs';
import { SectionCard, EmptyState, StatusTag } from '../../common';
import { formatMoney, enrichProduct, useStoreSearch } from './productPriceExplorer.controller';
import { PriceValue, VarianceBadge, SuspectChip, DecisionChip, PromoTag } from './PriceBadges';

function ObservationReviewActions({ row, productId, onReviewObservation, reviewing }) {
    if (!onReviewObservation) return null;
    const storeCheckId = row.storeCheckId;
    const pid = row.productId || productId;
    if (!storeCheckId || !pid) return null;

    const review = (decision) => onReviewObservation({ storeCheckId, productId: pid, decision });

    if (row.decision === 'excluded') {
        return (
            <Space size={4}>
                <Button size="small" type="text" icon={<Undo2 />} loading={reviewing} onClick={() => review('clear')}>
                    Incluir de nuevo
                </Button>
            </Space>
        );
    }

    return (
        <Space size={4}>
            {row.decision !== 'validated' && (
                <Tooltip title="Incluir en el promedio">
                    <Button size="small" type="text" icon={<Check />} loading={reviewing} onClick={() => review('validated')}>
                        Validar
                    </Button>
                </Tooltip>
            )}
            <Tooltip title="Quitar del promedio">
                <Button size="small" type="text" danger icon={<Ban />} loading={reviewing} onClick={() => review('excluded')}>
                    Excluir
                </Button>
            </Tooltip>
            {row.decision === 'validated' && (
                <Tooltip title="Restablecer revisión">
                    <Button
                        size="small"
                        type="text"
                        icon={<Undo2 />}
                        aria-label="Restablecer revisión"
                        loading={reviewing}
                        onClick={() => review('clear')}
                    />
                </Tooltip>
            )}
        </Space>
    );
}

/** Small "label / value" cell used in the product card and detail metrics. */
const Metric = ({ label, children, hint }) => (
    <div className="min-w-0">
        <span className="block text-[11px] font-medium uppercase tracking-wide text-surface-400">{label}</span>
        <div className="mt-0.5">{children}</div>
        {hint}
    </div>
);

function ProductCard({ product, selected, onClick, globalTimeline = [] }) {
    const enriched = enrichProduct(product, globalTimeline);
    const hasData = enriched.observationCount > 0;
    const listRef = enriched.suggestedPrice ?? enriched.listPrice;
    const reportingStores = enriched.storeCount;

    return (
        <button
            type="button"
            onClick={() => onClick(product)}
            className={`w-full text-left rounded-xl border p-3.5 transition-colors ${
                selected
                    ? 'border-primary-300 bg-primary-50 ring-1 ring-primary-200'
                    : hasData
                      ? 'border-surface-200 bg-white hover:border-surface-300 hover:bg-surface-25'
                      : 'border-surface-200 bg-surface-50 opacity-70 hover:opacity-100'
            }`}>
            <div className="flex items-start justify-between gap-2 mb-2.5">
                <span className="text-sm font-medium text-surface-900 line-clamp-2">{product.name}</span>
                {hasData ? <VarianceBadge value={enriched.variancePercent} /> : <StatusTag>Sin datos</StatusTag>}
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
                <Metric label="Lista">
                    <span className="tabular-nums font-medium text-primary-600">{formatMoney(listRef)}</span>
                </Metric>
                <Metric label="Promedio producto">
                    <span className="tabular-nums font-medium text-surface-900">
                        {hasData ? formatMoney(enriched.averageObservedPrice) : '—'}
                    </span>
                </Metric>
            </div>

            <div className="flex items-center gap-2 mt-2.5 text-[11px] text-surface-500 tabular-nums">
                <span>
                    {reportingStores} piso{reportingStores !== 1 ? 's' : ''}
                </span>
                <span className="text-surface-300">·</span>
                <span>
                    {enriched.observationCount} precio{enriched.observationCount !== 1 ? 's' : ''} registrado
                    {enriched.observationCount !== 1 ? 's' : ''}
                </span>
            </div>
        </button>
    );
}

function ProductStorePriceTable({ stores = [], listPrice, observationCount, excludedCount = 0, productId, onReviewObservation, reviewing }) {
    const { search, setSearch, filtered } = useStoreSearch(stores);

    const columns = [
        {
            title: '#',
            key: 'index',
            width: 52,
            render: (_, __, index) => <span className="text-xs tabular-nums text-surface-400">{index + 1}</span>,
        },
        {
            title: 'Piso de venta',
            dataIndex: 'storeName',
            key: 'storeName',
            ellipsis: true,
            render: (name) => (
                <div className="flex items-center gap-2 min-w-0">
                    <Store className="shrink-0 text-[13px] text-surface-400" />
                    <span className="truncate text-sm font-medium text-surface-900">{name}</span>
                </div>
            ),
        },
        {
            title: 'Precio registrado (USD)',
            dataIndex: 'latestPrice',
            key: 'latestPrice',
            width: 150,
            align: 'right',
            render: (v, row) => <PriceValue amount={v} row={row} strong />,
            sorter: (a, b) => (a.latestPrice || 0) - (b.latestPrice || 0),
        },
        {
            title: 'Precio lista (USD)',
            key: 'listPrice',
            width: 130,
            align: 'right',
            render: () => <span className="tabular-nums text-primary-600">{formatMoney(listPrice)}</span>,
        },
        {
            title: 'Δ vs lista',
            dataIndex: 'variancePercent',
            key: 'variancePercent',
            width: 120,
            render: (v) => <VarianceBadge value={v} />,
            sorter: (a, b) => (a.variancePercent ?? 0) - (b.variancePercent ?? 0),
        },
        {
            title: 'Último registro',
            dataIndex: 'latestObservedAt',
            key: 'latestObservedAt',
            width: 130,
            render: (v) => <span className="tabular-nums text-surface-600">{v ? dayjs(v).format('DD/MM/YY') : '—'}</span>,
            sorter: (a, b) => new Date(a.latestObservedAt || 0) - new Date(b.latestObservedAt || 0),
        },
        {
            title: 'Registros',
            dataIndex: 'observationCount',
            key: 'observationCount',
            width: 100,
            align: 'right',
            render: (v) => (v > 1 ? <StatusTag tone="brand">{v}</StatusTag> : <span className="tabular-nums">{v || 1}</span>),
        },
    ];

    return (
        <div>
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <h4 className="m-0 text-sm font-semibold text-surface-900">
                        Pisos que reportaron este producto ({stores.length})
                    </h4>
                    <p className="m-0 mt-0.5 text-xs text-surface-500">
                        El promedio del producto se calcula sobre {observationCount} precio
                        {observationCount !== 1 ? 's' : ''} registrado{observationCount !== 1 ? 's' : ''} en USD
                        (bolívares convertidos con tasa BCV del día del registro)
                        {excludedCount > 0 ? ` · ${excludedCount} precio${excludedCount !== 1 ? 's' : ''} excluido${excludedCount !== 1 ? 's' : ''} del promedio` : ''}
                    </p>
                </div>
                <Input
                    allowClear
                    prefix={<Search className="text-surface-400" />}
                    placeholder="Buscar piso..."
                    className="w-56"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            <div className="overflow-hidden rounded-lg border border-surface-200">
                <Table
                    dataSource={filtered}
                    columns={columns}
                    rowKey="storeId"
                    size="middle"
                    pagination={{
                        pageSize: 25,
                        showSizeChanger: true,
                        pageSizeOptions: [25, 50, 100],
                        showTotal: (total) => `${total} piso${total !== 1 ? 's' : ''}`,
                    }}
                    scroll={{ x: 800 }}
                    locale={{ emptyText: <EmptyState icon={<Search />} title="Ningún piso coincide con la búsqueda" /> }}
                    expandable={{
                        expandedRowRender: (record) =>
                            (record.history || []).length === 0 ? null : (
                                <div className="py-1">
                                    <p className="m-0 mb-2 text-xs text-surface-500">
                                        Precios registrados en este piso · valida o excluye los sospechosos del promedio
                                    </p>
                                    <div className="space-y-1.5">
                                        {record.history.map((row, i) => (
                                            <div
                                                key={`${row.observedAt}-${i}`}
                                                className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${
                                                    row.decision === 'excluded'
                                                        ? 'border-surface-200 bg-surface-100 opacity-60'
                                                        : row.suspect
                                                          ? 'border-warning-border bg-warning-bg'
                                                          : 'border-surface-100 bg-surface-50'
                                                }`}>
                                                <span className="tabular-nums text-surface-500">{dayjs(row.observedAt).format('DD/MM/YY HH:mm')}</span>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <PriceValue amount={row.effectivePrice} row={row} strong />
                                                    <VarianceBadge value={row.variancePercent} />
                                                    {row.suspect && row.decision !== 'validated' && row.decision !== 'excluded' && (
                                                        <SuspectChip deviationPercent={row.deviationPercent} />
                                                    )}
                                                    <DecisionChip decision={row.decision} />
                                                    {row.isPromo && <PromoTag />}
                                                    <ObservationReviewActions
                                                        row={row}
                                                        productId={productId}
                                                        onReviewObservation={onReviewObservation}
                                                        reviewing={reviewing}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ),
                        rowExpandable: (record) => (record.history || []).length > 0,
                    }}
                />
            </div>
        </div>
    );
}

function ProductDetailPanel({ product, globalTimeline = [], onReviewObservation, reviewing }) {
    if (!product) return null;
    const enriched = enrichProduct(product, globalTimeline);
    const listRef = enriched.suggestedPrice ?? enriched.listPrice;
    const stores = enriched.storeBreakdown;
    const reportingStores = enriched.storeCount;
    const excludedCount = enriched.excludedCount || 0;
    const rawAverage = enriched.rawAverage;

    return (
        <div className="space-y-5">
            <div>
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h3 className="m-0 text-lg font-semibold tracking-tight text-surface-900">{enriched.name}</h3>
                        <span className="block font-mono text-xs text-surface-400">{enriched.productId}</span>
                    </div>
                    <VarianceBadge value={enriched.variancePercent} />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                    <div className="rounded-lg border border-surface-200 bg-surface-25 px-3 py-2.5">
                        <Metric label="Precio lista (USD)">
                            <span className="text-base font-semibold tabular-nums text-primary-600">{formatMoney(listRef)}</span>
                        </Metric>
                    </div>
                    <div className="rounded-lg border border-surface-200 bg-surface-25 px-3 py-2.5">
                        <Metric
                            label="Promedio limpio (USD)"
                            hint={
                                <>
                                    <span className="block mt-0.5 text-[11px] text-surface-500">
                                        {excludedCount > 0
                                            ? `sin ${excludedCount} precio${excludedCount !== 1 ? 's' : ''} excluido${excludedCount !== 1 ? 's' : ''}`
                                            : `sobre ${enriched.observationCount} precio${enriched.observationCount !== 1 ? 's' : ''}`}
                                    </span>
                                    {rawAverage != null && excludedCount > 0 && (
                                        <Tooltip title="Promedio bruto incluyendo precios excluidos">
                                            <span className="block text-[11px] tabular-nums text-surface-500">
                                                bruto: {formatMoney(rawAverage)}
                                            </span>
                                        </Tooltip>
                                    )}
                                </>
                            }>
                            <span className="text-base font-semibold tabular-nums text-surface-900">
                                {formatMoney(enriched.averageObservedPrice)}
                            </span>
                        </Metric>
                    </div>
                    <div className="rounded-lg border border-surface-200 bg-surface-25 px-3 py-2.5">
                        <Metric label="Pisos reportando">
                            <span className="text-base font-semibold tabular-nums text-surface-900">{reportingStores}</span>
                        </Metric>
                    </div>
                    <div className="rounded-lg border border-surface-200 bg-surface-25 px-3 py-2.5">
                        <Metric label="Rango en campo">
                            <span className="block text-sm font-semibold tabular-nums text-surface-900">
                                {enriched.minObservedPrice != null
                                    ? `${formatMoney(enriched.minObservedPrice)} – ${formatMoney(enriched.maxObservedPrice)}`
                                    : '—'}
                            </span>
                        </Metric>
                    </div>
                </div>
            </div>

            {stores.length === 0 && enriched.observationCount > 0 ? (
                <EmptyState
                    tone="danger"
                    icon={<TriangleAlert />}
                    description="Hay registros de precio pero no se pudieron agrupar por piso. Revisa la respuesta del API."
                />
            ) : stores.length === 0 ? (
                <EmptyState icon={<Store />} description="Sin precios de este producto en storechecks del período" />
            ) : (
                <ProductStorePriceTable
                    stores={stores}
                    listPrice={listRef}
                    observationCount={enriched.observationCount}
                    excludedCount={excludedCount}
                    productId={enriched.productId}
                    onReviewObservation={onReviewObservation}
                    reviewing={reviewing}
                />
            )}
        </div>
    );
}

export default function ProductPriceExplorer({
    products = [],
    selectedProductId,
    onSelectProduct,
    globalTimeline = [],
    onReviewObservation,
    reviewing = false,
    // anomalyThreshold is accepted for context; suspect flags come pre-computed from the API
    anomalyThreshold,
}) {
    const selectedProduct = products.find((p) => p.productId === selectedProductId) || null;
    const withData = products.filter((p) => p.observationCount > 0);
    const withoutData = products.filter((p) => p.observationCount === 0);

    if (!products.length) {
        return (
            <div className="shelf-card">
                <EmptyState icon={<Package />} description="No hay productos en esta lista de precios" />
            </div>
        );
    }

    return (
        <div className="flex flex-col xl:flex-row gap-4">
            <div className="xl:w-[340px] shrink-0">
                <SectionCard
                    title={`Productos (${products.length})`}
                    description={`${withData.length} con precios en el período`}
                    icon={<Package />}
                    flush
                    className="overflow-hidden"
                    bodyClassName="p-3 max-h-[640px] overflow-y-auto">
                    <div className="space-y-2">
                        {withData.map((product) => (
                            <ProductCard
                                key={product.productId}
                                product={product}
                                globalTimeline={globalTimeline}
                                selected={selectedProductId === product.productId}
                                onClick={onSelectProduct}
                            />
                        ))}
                        {withoutData.length > 0 && (
                            <>
                                <span className="block px-1 pt-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-surface-400">
                                    Sin datos en el período
                                </span>
                                {withoutData.map((product) => (
                                    <ProductCard
                                        key={product.productId}
                                        product={product}
                                        globalTimeline={globalTimeline}
                                        selected={selectedProductId === product.productId}
                                        onClick={onSelectProduct}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                </SectionCard>
            </div>

            <div className="flex-1 min-w-0">
                <div className="shelf-card p-5 min-h-[400px]">
                    {selectedProduct ? (
                        <ProductDetailPanel
                            product={selectedProduct}
                            globalTimeline={globalTimeline}
                            onReviewObservation={onReviewObservation}
                            reviewing={reviewing}
                        />
                    ) : (
                        <EmptyState
                            icon={<MousePointerClick />}
                            description="Selecciona un producto para ver el promedio y la lista de pisos con precio"
                            className="py-16"
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export { VarianceBadge, formatMoney };
